import { __ } from '@wordpress/i18n';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import slugify from 'slugify';

// Components.
import Checkbox from '@/components/checkbox';
import Help from '@/components/help';
import Radio from '@/components/radio';

// Config.
import { FILTER_SHAPE } from '@/config/prop-types';

const FilterGroup = ({
  helpText,
  limit,
  onChange,
  options,
  title,
  type,
  values,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [subexpanded, setSubexpanded] = useState([]);
  const Component = type === 'checkbox' ? Checkbox : Radio;

  /**
   * Helper function for a subexpansion toggle.
   * @param {string} parent - The slug for the parent element.
   */
  const toggleSubexpansion = (parent) => (subexpanded.includes(parent)
    ? setSubexpanded(subexpanded.filter((slug) => slug !== parent))
    : setSubexpanded([...subexpanded, parent]));

  /**
   * Helper function for generating a set of inputs.
   * @param {Array} optionsSubset - The subset of options to render.
   * @returns {JSX.Element[]} An array of JSX elements.
   */
  const renderSubset = (optionsSubset) => optionsSubset.map(({ children, label, slug }) => (
    <>
      <Component
        checked={values.includes(slug)}
        key={slug}
        label={label}
        name={slugify(type === 'checkbox' ? `${title} ${slug}` : title)}
        onChange={onChange}
        value={slug}
      >
        {children.length > 0 ? (
          <>
            <button
              className="hst-button hst-button__toggle"
              onClick={() => toggleSubexpansion(slug)}
              type="button"
            >
              {subexpanded.includes(slug) ? (
                <>
                  <span aria-label={__('Collapse', 'hst')}>
                    -
                  </span>
                </>
              ) : (
                <>
                  <span aria-label={__('Expand', 'hst')}>
                    +
                  </span>
                </>
              )}
            </button>
            {subexpanded.includes(slug) ? renderSubset(children) : null}
          </>
        ) : null}
      </Component>
    </>
  ));

  return (
    <div
      ariaLabeledby={slugify(title)}
      className="hst-filter__group"
      role="group"
    >
      <h3
        aria-describedby={slugify(helpText)}
        className="hst-filter__group-label"
        id={slugify(title)}
      >
        <span>{title}</span>
      </h3>
      <Help text={helpText} />
      <span className="hst-spacer" />
      {!expanded ? renderSubset(options.slice(0, limit)) : renderSubset(options)}
      <span className="hst-spacer" />
      <button
        className="hst-button hst-button__show"
        onClick={() => setExpanded(!expanded)}
        type="button"
      >
        {!expanded ? __('+ show more', 'hst') : __('- show less', 'hst')}
      </button>
    </div>
  );
};

FilterGroup.propTypes = {
  helpText: PropTypes.string.isRequired,
  limit: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape(FILTER_SHAPE)).isRequired,
  title: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['checkbox', 'radio']).isRequired,
  values: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default FilterGroup;
